import { useClient, useQuery } from '@weenat/client'
import useDisplayParams from '@weenat/client/dist/hooks/useDisplayParams'
import { PlotOrStationSummary } from '@weenat/client/dist/resources/measurements'
import { isPlotSummary } from '@weenat/client/dist/resources/measurements.type'
import { Plot } from '@weenat/client/dist/resources/plots'
import { Id } from '@weenat/client/dist/resources/types'
import { drawShape, GMCircle, GMMultiPolygon, GMPolygon } from 'app/src/map/utils'
import isArray from 'lodash-es/isArray'
import isEmpty from 'lodash-es/isEmpty'
import isNil from 'lodash-es/isNil'
import { useEffect, useMemo, useRef } from 'react'
import { useTheme } from 'styled-components'
import { useBackgroundMapContext } from '../contexts/BackgroundMapContext'

const MIN_ZOOM_FOR_SHAPES = 14
const EMPTY_SHAPES: { [key: string]: Plot['shape'] } = {}

const getSummaryMetadataItem = (summary: PlotOrStationSummary) => {
  return isPlotSummary(summary) ? summary.metadata.plot : summary.metadata.station
}

interface UseDrawVisiblePlotShapesArg {
  currentOrganizationId: Id | null
  summaries: PlotOrStationSummary[]
}

const useDrawVisiblePlotShapes = ({
  currentOrganizationId,
  summaries
}: UseDrawVisiblePlotShapesArg) => {
  const theme = useTheme()
  const client = useClient()

  const displayPreferences = useDisplayParams()

  const {
    api: { map, maps }
  } = useBackgroundMapContext()

  let visiblePlotsIds: Id[] = []

  const getPlotIdsWithinBounds = () => {
    if (isNil(map) || isNil(maps)) return new Set<Id>()
    const bounds = map.getBounds()

    return summaries.reduce((acc, marker) => {
      const summary = marker

      const { location } = getSummaryMetadataItem(summary)

      if (!isNil(location) && !isNil(bounds)) {
        const [lng, lat] = location.coordinates
        if (bounds.contains(new maps.LatLng({ lat, lng })) && isPlotSummary(summary)) {
          acc.add(getSummaryMetadataItem(summary).id)
        }
      }

      return acc
    }, new Set<Id>())
  }

  const zoom = map?.getZoom()

  if (!isNil(zoom) && zoom > MIN_ZOOM_FOR_SHAPES) {
    const newIds = getPlotIdsWithinBounds()
    visiblePlotsIds = Array.from(newIds)
  }

  const drawnShapes = useRef<{ [key: number]: GMCircle | GMPolygon | GMMultiPolygon }>({})

  const visiblePlotsRequest = useQuery(
    client.plots.getAllPages({
      ids: visiblePlotsIds,
      withShape: true,
      organizationId: currentOrganizationId as number
    }),
    {
      enabled:
        !isEmpty(visiblePlotsIds) && !isNil(currentOrganizationId) && !displayPreferences.hidePlots
    }
  )

  const visibleShapeRecord: { [key: number]: Plot['shape'] } = useMemo(() => {
    if (!isNil(visiblePlotsRequest.data) && !displayPreferences.hidePlots) {
      return visiblePlotsRequest.data.reduce(
        (acc, plot) => {
          acc[plot.id] = plot.shape
          return acc
        },
        {} as { [key: number]: Plot['shape'] }
      )
    } else {
      return EMPTY_SHAPES
    }
  }, [visiblePlotsRequest.data, displayPreferences.hidePlots])

  const removeDrawnShapes = (shapesToExclude?: number[]) => {
    Object.entries(drawnShapes.current)
      .filter(([id]) => !shapesToExclude?.includes(Number(id)))
      .forEach(([id, drawnShape]) => {
        if (isNil(drawnShape)) return

        if (isArray(drawnShape)) {
          drawnShape.forEach((gmShape) => {
            gmShape.setMap(null)
          })
        } else {
          drawnShape.setMap(null)
        }
        delete drawnShapes.current[Number(id)]
      })
  }

  // Cleaning up everything when the component unmounts
  useEffect(() => {
    return () => {
      removeDrawnShapes()
    }
  }, [])

  // Every time a fetch towards plots occurs :
  // We remove shape that are no longer in view
  // We draw the new shapes
  useEffect(
    () => {
      if (isEmpty(visibleShapeRecord) && !isEmpty(drawnShapes.current)) {
        removeDrawnShapes()
      } else if (!isEmpty(visibleShapeRecord)) {
        // remove shapes that are no longer in view
        removeDrawnShapes(Object.keys(visibleShapeRecord).map((id) => Number(id)))
        if (!isNil(map) && !isNil(maps)) {
          Object.entries(visibleShapeRecord)
            .filter(([id]) => !Object.keys(drawnShapes.current).includes(id))
            .forEach(([id, shape]) => {
              if (!isNil(shape)) {
                const drawing = drawShape({
                  map,
                  maps,
                  theme,
                  shape
                })
                drawnShapes.current[Number(id)] = drawing.shape
              }
            })
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleShapeRecord]
  )
}

export default useDrawVisiblePlotShapes
